import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { brand, listingPortals, pageMeta } from "@/data/mockData";
import { useListings } from "@/lib/content";
import { formatPrice, formatSqft, stripHtml, truncate } from "@/lib/format";

const FILTERS = [
  "All",
  "HDB (Resale)",
  "Condo (Resale)",
  "Condo (New Launch)",
  "Landed",
  "Commercial",
];

function ListingCard({ listing, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.45, delay: index * 0.06 }}
      data-testid={`listing-card-${listing.id}`}
      className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
    >
      <Link
        to={`/listings/${listing.id}`}
        className="relative block aspect-[4/3] overflow-hidden bg-gray-100"
        data-testid={`listing-detail-link-${listing.id}`}
      >
        <img
          src={listing.image}
          alt={listing.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <span className="absolute top-4 left-4 bg-white/95 text-[#0a2540] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest rounded-full">
          {listing.type}
        </span>
      </Link>
      <div className="p-6">
        <h3 className="font-serif text-lg text-[#0a2540] leading-snug mb-2">
          {listing.title}
        </h3>
        <p className="text-xs text-slate-500 mb-4 flex items-center gap-1.5">
          <i className="fa-solid fa-location-dot text-[#0a2540]" aria-hidden="true" />
          {listing.location}
        </p>
        <div className="flex items-center gap-4 text-xs text-slate-500 mb-4 pb-4 border-b border-gray-100">
          <span className="flex items-center gap-1.5">
            <i className="fa-solid fa-bed text-slate-400" aria-hidden="true" />
            {listing.bedrooms}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="fa-solid fa-bath text-slate-400" aria-hidden="true" />
            {listing.bathrooms}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="fa-solid fa-ruler-combined text-slate-400" aria-hidden="true" />
            {formatSqft(listing.size)}
          </span>
        </div>
        <p className="text-xs text-slate-600 leading-relaxed mb-5 line-clamp-2">
          {truncate(stripHtml(listing.description), 120)}
        </p>
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Price</div>
            <div className="font-serif text-xl text-[#0a2540] leading-none">{formatPrice(listing.price)}</div>
          </div>
          <a
            href={`${brand.whatsapp}?text=Hi%20Johnny%2C%20I%27d%20like%20to%20ask%20about%20${encodeURIComponent(listing.title)}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`listing-ask-${listing.id}`}
            className="inline-flex items-center gap-1.5 bg-[#ffde59] text-[#023e8a] px-4 py-2.5 text-xs font-semibold rounded-full hover:shadow-md transition-all"
          >
            <i className="fa-brands fa-whatsapp" aria-hidden="true" />
            Ask Johnny
          </a>
        </div>
      </div>
    </motion.article>
  );
}

export default function Listings() {
  const [filter, setFilter] = useState("All");
  const { data: listings, loading } = useListings();
  const filtered = filter === "All" ? listings : listings.filter((l) => l.type === filter);

  return (
    <>
      <SEO {...pageMeta.listings} />

      <section className="bg-white pt-20 pb-12 md:py-24" data-testid="listings-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold mb-4">
              Featured Listings
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              Hand-picked property opportunities.
            </h1>
            <p className="mt-7 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              A curated selection of HDB resale, private condos, and investment-grade units across Singapore.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fafafa] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter chips */}
          <div className="flex flex-wrap justify-center gap-2 mb-12" data-testid="listings-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`listing-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`px-5 py-2.5 text-xs font-medium uppercase tracking-wider rounded-full transition-all ${
                  filter === f
                    ? "bg-[#0a2540] text-white"
                    : "bg-white text-slate-600 border border-gray-200 hover:border-[#0a2540]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading && listings.length === 0 ? (
            <div className="text-center py-20 text-slate-400 text-sm">Loading listings…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500 text-sm">
              No listings in this category right now. Check back soon.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((l, i) => (
                <ListingCard listing={l} index={i} key={l.id} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* External portals */}
      <section className="bg-white py-20 md:py-28" data-testid="external-portals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-4xl mx-auto mb-14">
            <div className="flex flex-col items-center justify-center mb-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#cfa94a] font-semibold mb-2">
                MORE LISTINGS
              </div>
              <div className="flex items-center w-full max-w-[5rem] mx-auto">
                <div className="flex-grow h-px bg-[#cfa94a]"></div>
                <div className="text-[8px] text-[#cfa94a] px-2">♦</div>
                <div className="flex-grow h-px bg-[#cfa94a]"></div>
              </div>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              Browse Johnny's listings on every major portal<span className="text-[#cfa94a]">.</span>
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Verified profile, agent reviews, and live listings across Singapore's leading property platforms.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {listingPortals.map((p, i) => (
              <motion.a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`portal-link-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="advisory-card block bg-white border border-gray-100 rounded-2xl p-7 text-center shadow-sm hover:shadow-lg transition-all"
              >
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="advisory-icon h-36 sm:h-40 lg:h-48 w-full object-contain mb-5 mx-auto select-none"
                />
                <h3 className="font-serif text-xl sm:text-2xl text-[#0a2540] leading-tight mb-2">{p.name}</h3>
                <p className="text-[13px] sm:text-sm text-slate-500 leading-relaxed mb-4">{p.description}</p>
                <div className="h-[2px] w-6 mx-auto bg-[#ffde59] mb-4" />
                <span className="inline-flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#0a2540]">
View Listings              <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      <CTABanner headline="Don't see what you're looking for?" subcopy="Tell Johnny what you have in mind — he'll source off-market and pre-launch opportunities tailored to your goals." />
    </>
  );
}
